import React, { useEffect, useMemo, useState } from 'react';
import { getDistrict, getProvince, getWard, getCountry } from 'services/location.service';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import { useFormContext, useWatch } from 'react-hook-form';

// Kept for existing forms that explicitly choose Việt Nam as their new-record default.
export const DEFAULT_COUNTRY_ID = 6;

const masterDataCache = new Map();

const getSelectValue = (value) => {
  if (value && typeof value === 'object') {
    return value.value ?? value.id ?? null;
  }

  return value ?? null;
};

const getResponseItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const mapData = (response) =>
  getResponseItems(response)
    .map(({ name, id, label, value, ...props }) => ({
      ...props,
      label: label ?? name,
      value: value ?? id,
    }))
    .filter((item) => item.value !== undefined && item.value !== null && item.label);

const loadCachedOptions = (key, loader) => {
  if (masterDataCache.has(key)) {
    return masterDataCache.get(key);
  }

  const request = Promise.resolve()
    .then(loader)
    .then(mapData)
    .then((options) => {
      if (!options.length) masterDataCache.delete(key);
      return options;
    })
    .catch((error) => {
      masterDataCache.delete(key);
      throw error;
    });

  masterDataCache.set(key, request);
  return request;
};

const loadAddressOptions = (type, parentValue, selectedValue) => {
  const parentId = getSelectValue(parentValue);
  const selectedId = getSelectValue(selectedValue);

  switch (type) {
    case 'country':
      return loadCachedOptions('country', () => getCountry());
    case 'province':
      if (!parentId) return Promise.resolve([]);
      return loadCachedOptions(`province:${parentId}`, () => getProvince({ country_id: parentId }));
    case 'district':
      if (!parentId) return Promise.resolve([]);
      return loadCachedOptions(`district:${parentId}`, () => getDistrict({ parent_id: parentId }));
    case 'ward':
      if (!parentId) return Promise.resolve([]);
      return loadCachedOptions(`ward:${parentId}:${selectedId || ''}`, () =>
        getWard({
          province_id: parentId,
          ...(selectedId ? { ward_id: selectedId } : {}),
        }),
      );
    default:
      return Promise.resolve([]);
  }
};

function BWAddress(props) {
  const {
    type = 'province',
    field,
    parentField,
    placeholder = 'Chọn',
    loading: externalLoading,
    ...selectProps
  } = props || {};
  const methods = useFormContext();
  const defaultParentField = type === 'province' ? 'country_id' : 'province_id';
  const resolvedParentField = parentField || defaultParentField;
  const watchedParentValue = useWatch({
    control: methods.control,
    name: resolvedParentField,
    disabled: type === 'country',
  });
  const parentValue = type === 'country' ? null : watchedParentValue;
  const selectedValue = useWatch({
    control: methods.control,
    name: field,
    disabled: type !== 'ward' || !field,
  });

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const normalizedParentValue = useMemo(
    () => (type === 'country' ? null : getSelectValue(parentValue)),
    [parentValue, type],
  );
  const normalizedSelectedValue = useMemo(
    () => (type === 'ward' ? getSelectValue(selectedValue) : null),
    [selectedValue, type],
  );

  useEffect(() => {
    let mounted = true;

    if (type !== 'country' && !normalizedParentValue) {
      setOptions([]);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    loadAddressOptions(type, normalizedParentValue, normalizedSelectedValue)
      .then((loadedOptions) => {
        if (mounted) setOptions(loadedOptions);
      })
      .catch(() => {
        if (mounted) setOptions([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [normalizedParentValue, normalizedSelectedValue, type]);

  return (
    <FormSelect
      {...selectProps}
      field={field}
      placeholder={placeholder}
      list={options}
      loading={Boolean(externalLoading) || loading}
      showUnresolvedValue={false}
    />
  );
}

export default BWAddress;
